import { FC, lazy, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useScrollLock } from '@platform/ui'
import { StringParam, useQueryParam } from 'use-query-params'
import { CustomSuspense } from '@/components/CustomSuspense/CustomSuspense'
import {
  addFullScreenButtons,
  createPdfOpenButtonIfNotExist,
  findPdfName,
  makeTargetBlankAllLinks,
  normalizeImageSrcToOrigin,
  setPdfOpenButtonClickHandler,
} from '@/utils/richTextUtils'
import { toRelativeURL } from '../utils/toRelativeURL'

const PDFViewer = lazy(() => import('@lms/lms-pdf-viewer').then((module) => ({ default: module.PDFViewer })))

export const PDF_URL_QUERY_STR = 'pdfUrl'

type Props = {
  contentClassName: string
  html: {
    __html: string
  }
  allowedExternalSources: string[]
}

export const ArticleViewerContentRendererComponent: FC<Props> = ({
  contentClassName,
  html,
  allowedExternalSources,
}) => {
  const { i18n } = useTranslation()

  const containerRef = useRef<HTMLDivElement>(null)
  const [pdfTitle, setPdfTitle] = useState('')
  const [pdfURL, setPdfURL] = useQueryParam(PDF_URL_QUERY_STR, StringParam)

  const handleClick = useCallback(
    (url: string) => {
      const relativeURL = toRelativeURL(url)

      if (relativeURL) {
        setPdfURL(relativeURL)
      } else {
        console.warn(`Blocked invalid PDF URL: ${url}`)
      }
    },
    [setPdfURL],
  )

  const handleClosePDF = useCallback(() => {
    setPdfURL(null)
  }, [setPdfURL])

  const { lockScroll, unlockScroll } = useScrollLock()

  const setPdfTitleFromContent = useCallback(() => {
    if (!containerRef.current || !pdfURL) {
      return
    }

    const contentArea = containerRef.current.querySelector<HTMLDivElement>(`.${contentClassName}`)
    if (!contentArea) {
      return
    }

    const title = findPdfName(contentArea, pdfURL)
    setPdfTitle(title || '')
  }, [pdfURL, contentClassName])

  useLayoutEffect(() => {
    if (!html) {
      return
    }

    if (!containerRef.current) {
      return
    }

    const contentArea = containerRef.current.querySelector<HTMLDivElement>(`.${contentClassName}`)

    if (!contentArea) {
      return
    }

    makeTargetBlankAllLinks(contentArea)
    normalizeImageSrcToOrigin(contentArea, allowedExternalSources)
    // this createPdfOpenButtonIfNotExist method for pdf attachments that does not include Open button
    createPdfOpenButtonIfNotExist(contentArea, handleClick)
    setPdfOpenButtonClickHandler(contentArea, handleClick)
    // this addFullScreenButtons method adds/overwrites full screen buttons for tables
    addFullScreenButtons(contentArea, lockScroll, unlockScroll)
    setPdfTitleFromContent()
  }, [
    contentClassName,
    html,
    i18n.language,
    handleClick,
    lockScroll,
    unlockScroll,
    setPdfTitleFromContent,
    allowedExternalSources,
  ])

  useEffect(() => {
    if (!pdfURL) {
      return
    }

    const relativeURL = toRelativeURL(pdfURL)

    if (!relativeURL) {
      console.warn(`Blocked invalid PDF URL: ${pdfURL}`)
      setPdfURL(null)
    } else if (pdfURL !== relativeURL) {
      setPdfURL(relativeURL)
    }
  }, [pdfURL, setPdfURL])

  return (
    <div ref={containerRef}>
      {pdfURL && (
        <CustomSuspense>
          <PDFViewer title={pdfTitle} src={pdfURL} onClosePdf={handleClosePDF} />
        </CustomSuspense>
      )}
      <div dangerouslySetInnerHTML={html} className={contentClassName} />
    </div>
  )
}
